// Users & permissions (mock 09). Module 17: effective-access preview, onboarding by activation
// link (PD-009), grant issue/revoke incl. site administrators (PD-005/PD-006/PD-014), specialist
// roles (PD-004), deactivation with last-administrator protection, read-only security policy (PD-002).
import type {
  ActivationIssued,
  CreateUserResponse,
  GrantResponse,
  POLICY_BOUNDS,
  SecurityPolicyResponse,
  UserDetailResponse,
  UserListResponse,
  UserSummary,
} from '@passage/contracts';
import type { MessageKey } from '@passage/i18n';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { ApiRequestError, api, get, newIdempotencyKey } from '../api.ts';
import { useSiteName } from '../components/Shell.tsx';
import { Dialog, ErrorState, ErrorSummary, Field, Loading, PageHead, StatusBadge } from '../components/ui.tsx';
import { useI18n } from '../i18n.tsx';
import { useSession } from '../session.tsx';
import { UsersTabs } from './People.tsx';
import { UserIdentities } from './Identity.tsx';

// Kept in step with packages/domain (SITE_ADMIN_GRANTABLE, tenant-scope experiences, specialist roles).
const GRANTABLE = ['org-admin', 'site-admin', 'host', 'reception', 'approver', 'guard', 'permit', 'emergency', 'analyst', 'auditor', 'privacy', 'operator'];
const SITE_ADMIN_GRANTABLE = ['host', 'reception', 'approver', 'guard', 'permit', 'emergency', 'analyst', 'auditor', 'privacy', 'operator'];
const TENANT_WIDE_ALLOWED = new Set(['org-admin', 'analyst', 'auditor', 'privacy']);
const SPECIALISTS: ReadonlyArray<{ id: string; scope: 'site' | 'tenant' | 'either' }> = [
  { id: 'identity-verifier', scope: 'either' },
  { id: 'credential-issuer', scope: 'either' },
  { id: 'privacy-authority', scope: 'tenant' },
  { id: 'report-exporter', scope: 'either' },
  { id: 'sensitive-auditor', scope: 'either' },
  { id: 'muster-exporter', scope: 'either' },
  { id: 'approval-coordinator', scope: 'either' },
  { id: 'delegated-host', scope: 'site' },
  { id: 'company-suspension', scope: 'tenant' },
  { id: 'barrier-operator', scope: 'site' }, // PD-160
  { id: 'security-supervisor', scope: 'either' }, // PD-160
  { id: 'screening-list-manager', scope: 'either' }, // PD-290
  { id: 'restricted-profile-reader', scope: 'either' }, // PD-306
  { id: 'privacy-administrator', scope: 'tenant' }, // PD-252
  { id: 'protocol-officer', scope: 'either' }, // PD-053, PD-351 (ADR-027)
  { id: 'hr-assessor', scope: 'either' }, // PD-356 (ADR-027)
  { id: 'profile-steward', scope: 'either' }, // PD-372 (ADR-028)
  { id: 'configuration-promoter', scope: 'tenant' }, // PD-558 (opsview stream)
];

function useGrantLabel() {
  const { tk } = useI18n();
  return (g: { kind?: string; id?: string; code?: string }) => {
    const code = g.code ?? g.id ?? '';
    return g.kind === 'specialist' ? tk(`specialist.${code}`) : g.kind === 'capability' ? code : tk(`role.${code}.title`);
  };
}

export function activationUrl(token: string): string {
  return `${window.location.origin}/activate#token=${token}`;
}

export function UsersPage() {
  const { scopeKey, can, canAnywhere } = useSession();
  const { t, tk } = useI18n();
  const siteName = useSiteName();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<UserSummary | null>(null);
  const [filter, setFilter] = useState('');
  const [creating, setCreating] = useState(false);
  const [issued, setIssued] = useState<{ name: string; activation: ActivationIssued } | null>(null);
  const users = useInfiniteQuery({
    queryKey: [...scopeKey, 'users'],
    queryFn: ({ pageParam }) => get<UserListResponse>(`/api/v1/users?limit=50${pageParam ? `&after=${pageParam}` : ''}`),
    initialPageParam: '' as string,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const all = users.data?.pages.flatMap((p) => p.items) ?? [];
  const needle = filter.trim().toLowerCase();
  const visible = needle ? all.filter((u) => `${u.displayName} ${u.email}`.toLowerCase().includes(needle)) : all;
  const partial = !can('user.read') && canAnywhere('user.directory.read');

  return (
    <>
      <PageHead
        eyebrow={t('users.eyebrow')}
        title={t('users.title')}
        intro={t('users.intro')}
        actions={
          can('user.create') ? (
            <button className="button primary" type="button" onClick={() => setCreating(true)}>
              {t('users.create')}
            </button>
          ) : null
        }
      />
      <UsersTabs />
      <div className="scope-note">
        <strong>{t('users.note.title')}</strong>
        <p>{t('users.note.body')}</p>
        {partial ? <p>{t('access.hiddenScopes')}</p> : null}
      </div>
      <div className="cols">
        <section className="panel" aria-labelledby="users-title">
          <div className="panel-head">
            <div>
              <h2 id="users-title">{t('users.panel')}</h2>
              <p>{t('users.panelSub')}</p>
            </div>
          </div>
          <div className="pad" style={{ paddingBlockEnd: 0 }}>
            <Field label={t('common.search')}>
              {(p) => <input {...p} type="search" placeholder={t('users.searchPlaceholder')} value={filter} onChange={(e) => setFilter(e.target.value)} style={{ inlineSize: '100%' }} />}
            </Field>
          </div>
          {users.isPending ? (
            <Loading label={t('users.loading')} />
          ) : users.isError ? (
            <div className="pad">
              <ErrorState error={users.error} retry={() => void users.refetch()} />
            </div>
          ) : visible.length === 0 ? (
            <div className="empty">
              <h3>{t('users.none')}</h3>
            </div>
          ) : (
            <div className="tablewrap" tabIndex={0} aria-label={t('users.tableLabel')} data-scroll-hint={t('common.scrollHint')}>
              <table>
                <thead>
                  <tr>
                    <th scope="col">{t('users.col.user')}</th>
                    <th scope="col">{t('users.col.responsibilities')}</th>
                    <th scope="col">{t('users.col.state')}</th>
                    <th scope="col">{t('users.col.action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((u) => (
                    <tr key={u.id} aria-selected={selected?.id === u.id}>
                      <td>
                        <strong>{u.displayName}</strong>
                        <small data-ltr>{u.email}</small>
                      </td>
                      <td>
                        {u.experiences.length || u.specialistRoles.length ? (
                          <>
                            {u.experiences.map((e) => (
                              <div key={`${e.id}${e.siteId}`}>
                                {tk(`role.${e.id}.title`)} · {siteName(e.siteId)}
                              </div>
                            ))}
                            {u.specialistRoles.map((e) => (
                              <div key={`s${e.id}${e.siteId}`}>
                                <span className="badge amber">{tk(`specialist.${e.id}`)}</span> · {siteName(e.siteId)}
                              </div>
                            ))}
                          </>
                        ) : (
                          <span className="badge">{t('status.noGrants')}</span>
                        )}
                      </td>
                      <td>
                        <StatusBadge status={u.status} />
                      </td>
                      <td>
                        <div className="actions">
                          <button className="button small" type="button" onClick={() => setSelected(u)} aria-label={t('users.previewLabel', { name: u.displayName })}>
                            {t('users.preview')}
                          </button>
                          <Link className="button small" to={`/users/${u.id}`} aria-label={t('users.manageLabel', { name: u.displayName })}>
                            {t('users.manage')}
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="panel-foot">
            {t('users.loaded', { count: all.length })}
            {users.hasNextPage ? (
              <>
                {' · '}
                <button className="button small" type="button" onClick={() => void users.fetchNextPage()} disabled={users.isFetchingNextPage}>
                  {t('common.loadMore')}
                </button>
              </>
            ) : null}
          </div>
        </section>
        <aside className="panel" aria-labelledby="review-title" aria-live="polite">
          <div className="panel-head">
            <h2 id="review-title">{t('users.review')}</h2>
          </div>
          <div className="pad">{selected ? <AccessPreview userId={selected.id} /> : <p>{t('users.reviewHint')}</p>}</div>
        </aside>
      </div>
      {can('security.policy.read') ? <PolicyPanel /> : null}
      {creating ? (
        <CreateUserDialog
          onClose={() => setCreating(false)}
          onCreated={(r) => {
            setCreating(false);
            setIssued({ name: r.user.displayName, activation: r.activation });
            void qc.invalidateQueries({ queryKey: [...scopeKey, 'users'] });
          }}
        />
      ) : null}
      {issued ? <ActivationLinkDialog name={issued.name} activation={issued.activation} onClose={() => setIssued(null)} /> : null}
    </>
  );
}

function PolicyPanel() {
  const { scopeKey } = useSession();
  const { t } = useI18n();
  const q = useQuery({ queryKey: [...scopeKey, 'security-policy'], queryFn: () => get<SecurityPolicyResponse>('/api/v1/security-policy') });
  return (
    <section className="panel" aria-labelledby="policy-title">
      <div className="panel-head">
        <div>
          <h2 id="policy-title">{t('policy.title')}</h2>
          <p>{t('policy.intro')}</p>
        </div>
        {q.data ? <span className="badge blue">{t(q.data.source === 'organisation' ? 'policy.source.organisation' : 'policy.source.platform')}</span> : null}
      </div>
      <div className="pad">{q.isPending ? <Loading /> : q.isError ? <ErrorState error={q.error} /> : <PolicyFacts policy={q.data} />}</div>
    </section>
  );
}

export const POLICY_LABELS: Record<keyof typeof POLICY_BOUNDS, MessageKey> = {
  sessionIdleMinutes: 'policy.sessionIdle',
  sessionAbsoluteHours: 'policy.sessionAbsolute',
  loginMaxFailures: 'policy.maxFailures',
  loginFailureWindowMinutes: 'policy.window',
  loginLockMinutes: 'policy.lock',
  networkMaxFailures: 'policy.networkFailures',
  passwordMinLength: 'policy.passwordMin',
  activationLinkHours: 'policy.activationHours',
  siteGrantMaxDays: 'policy.siteGrantMaxDays',
};

export function PolicyFacts({ policy }: { policy: SecurityPolicyResponse }) {
  const { t, number } = useI18n();
  return (
    <>
      <dl className="definition">
        <div>
          <dt>{t('policy.mfaRequirement')}</dt>
          <dd>{t(`policy.mfa.${policy.mfaRequirement}`)}</dd>
        </div>
        {(Object.keys(POLICY_LABELS) as Array<keyof typeof POLICY_LABELS>).map((k) => (
          <div key={k}>
            <dt>{t(POLICY_LABELS[k])}</dt>
            <dd>{number(policy[k])}</dd>
          </div>
        ))}
      </dl>
      <p className="tiny" style={{ marginBlockStart: 14 }}>
        {t('policy.version', { version: policy.version })}
      </p>
    </>
  );
}

function AccessPreview({ userId }: { userId: string }) {
  const { scopeKey } = useSession();
  const { t } = useI18n();
  const siteName = useSiteName();
  const q = useQuery({ queryKey: [...scopeKey, 'user', userId], queryFn: () => get<UserDetailResponse>(`/api/v1/users/${userId}`) });
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorState error={q.error} />;
  const u = q.data;
  const siteEntries = Object.entries(u.effectiveAccess.sites);
  return (
    <dl className="definition" style={{ gridTemplateColumns: '1fr' }}>
      <div>
        <dt>{t('access.user')}</dt>
        <dd>
          {u.displayName} · <StatusBadge status={u.status} />
        </dd>
      </div>
      {!u.partialView ? (
        <div>
          <dt>{t('access.orgWide')}</dt>
          <dd>{u.effectiveAccess.tenant.length ? <ActionList actions={u.effectiveAccess.tenant} /> : t('access.nothingOrgWide')}</dd>
        </div>
      ) : null}
      {siteEntries.map(([site, actions]) => (
        <div key={site}>
          <dt>{siteName(site)}</dt>
          <dd>
            <ActionList actions={actions} />
          </dd>
        </div>
      ))}
      <div>
        <dt>{t('access.notGranted')}</dt>
        <dd>
          {t('access.notGrantedBody')}
          {u.partialView ? ` ${t('access.hiddenScopes')}` : ''}
        </dd>
      </div>
    </dl>
  );
}

function ActionList({ actions }: { actions: readonly string[] }) {
  return (
    <span className="actions" style={{ gap: 4 }}>
      {actions.map((a) => (
        <span key={a} className="badge" data-ltr>
          {a}
        </span>
      ))}
    </span>
  );
}

export function UserDetailPage() {
  const { userId = '' } = useParams();
  const { scopeKey, session, can, canAnywhere } = useSession();
  const { t, date, dateTime } = useI18n();
  const siteName = useSiteName();
  const grantLabel = useGrantLabel();
  const qc = useQueryClient();
  const key = [...scopeKey, 'user', userId];
  const q = useQuery({ queryKey: key, queryFn: () => get<UserDetailResponse>(`/api/v1/users/${userId}`) });
  const [issuing, setIssuing] = useState<'experience' | 'specialist' | null>(null);
  const [revoking, setRevoking] = useState<GrantResponse | null>(null);
  const [deactivating, setDeactivating] = useState(false);
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [mfaResetting, setMfaResetting] = useState(false);
  const [issued, setIssued] = useState<ActivationIssued | null>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: key });
    void qc.invalidateQueries({ queryKey: [...scopeKey, 'users'] });
  };
  if (q.isPending) return <Loading label={t('user.loading')} />;
  if (q.isError) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  const u = q.data;
  const self = u.id === session.user.id;
  const canGrant = can('grant.issue') || canAnywhere('grant.site.issue');
  const canRevokeGrant = (g: GrantResponse) =>
    g.kind === 'specialist' || g.kind === 'capability'
      ? can('grant.sensitive.issue')
      : can('grant.revoke') || (g.siteId !== null && SITE_ADMIN_GRANTABLE.includes(g.code) && can('grant.site.revoke', g.siteId));
  return (
    <>
      <PageHead
        eyebrow={t('user.eyebrow')}
        title={u.displayName}
        intro={<span data-ltr>{u.email}</span>}
        actions={
          !self && u.status !== 'disabled' ? (
            <>
              {canGrant ? (
                <button className="button primary" type="button" onClick={() => setIssuing('experience')}>
                  {t('user.grant')}
                </button>
              ) : null}
              {can('grant.sensitive.issue') ? (
                <button className="button" type="button" onClick={() => setIssuing('specialist')}>
                  {t('user.grantSpecialist')}
                </button>
              ) : null}
              {can('user.credential.reset') ? (
                <>
                  <button className="button" type="button" onClick={() => setResetting(true)}>
                    {t('user.resetPassword')}
                  </button>
                  <button className="button" type="button" onClick={() => setMfaResetting(true)}>
                    {t('mfa.reset')}
                  </button>
                </>
              ) : null}
              {can('session.revoke') && u.activeSessionCount !== null ? (
                <button className="button" type="button" onClick={() => setSessionsOpen(true)} disabled={u.activeSessionCount === 0}>
                  {t('user.revokeSessions', { count: u.activeSessionCount })}
                </button>
              ) : null}
              {can('user.deactivate') ? (
                <button className="button danger" type="button" onClick={() => setDeactivating(true)}>
                  {t('user.deactivate')}
                </button>
              ) : null}
            </>
          ) : null
        }
      />
      {self ? <div className="note">{t('user.self')}</div> : null}
      {u.partialView ? <div className="note">{t('access.hiddenScopes')}</div> : null}
      <div className="cols">
        <section className="panel" aria-labelledby="grants-title">
          <div className="panel-head">
            <div>
              <h2 id="grants-title">{t('user.grants')}</h2>
              <p>{t('user.grantsSub')}</p>
            </div>
            <StatusBadge status={u.status} />
          </div>
          <div className="tablewrap" tabIndex={0} aria-label={t('user.grantsLabel')} data-scroll-hint={t('common.scrollHint')}>
            <table>
              <thead>
                <tr>
                  <th scope="col">{t('user.col.responsibility')}</th>
                  <th scope="col">{t('user.col.scope')}</th>
                  <th scope="col">{t('user.col.validity')}</th>
                  <th scope="col">{t('user.col.state')}</th>
                  <th scope="col">{t('user.col.action')}</th>
                </tr>
              </thead>
              <tbody>
                {u.grants.length === 0 ? (
                  <tr>
                    <td colSpan={5}>{t('user.noGrants')}</td>
                  </tr>
                ) : (
                  u.grants.map((g) => (
                    <tr key={g.id}>
                      <td>
                        <strong>{g.kind === 'capability' ? <span data-ltr>{g.code}</span> : grantLabel(g)}</strong>
                        <small>
                          {t(`user.kind.${g.kind}` as MessageKey)} · {g.issuedReason}
                        </small>
                      </td>
                      <td>{siteName(g.siteId)}</td>
                      <td>
                        {t('user.from', { date: date(g.validFrom) })}
                        <small>{g.validUntil ? t('user.until', { date: dateTime(g.validUntil) }) : t('user.noEnd')}</small>
                      </td>
                      <td>{g.revokedAt ? <span className="badge red">{t('status.revoked')}</span> : g.active ? <span className="badge green">{t('status.effective')}</span> : <span className="badge amber">{t('status.notEffective')}</span>}</td>
                      <td>
                        {!g.revokedAt && !self && canRevokeGrant(g) ? (
                          <button className="button small" type="button" onClick={() => setRevoking(g)} aria-label={t('user.revokeLabel', { role: grantLabel(g), scope: siteName(g.siteId) })}>
                            {t('user.revoke')}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
        <aside className="panel" aria-labelledby="preview-title">
          <div className="panel-head">
            <h2 id="preview-title">{t('user.effective')}</h2>
          </div>
          <div className="pad">
            <AccessPreview userId={u.id} />
          </div>
        </aside>
      </div>
      <UserIdentities userId={u.id} />
      {issuing ? <IssueGrantDialog user={u} mode={issuing} onClose={() => setIssuing(null)} onSaved={refresh} /> : null}
      {revoking ? (
        <ReasonDialog
          title={t('user.revokeTitle', { role: grantLabel(revoking) })}
          subtitle={t('user.revokeSub', { name: u.displayName, scope: siteName(revoking.siteId) })}
          confirm={t('user.revokeConfirm')}
          path={`/api/v1/users/${u.id}/grants/${revoking.id}/revoke`}
          body={{}}
          onClose={() => setRevoking(null)}
          onSaved={refresh}
        />
      ) : null}
      {deactivating ? (
        <ReasonDialog title={t('user.deactivateTitle', { name: u.displayName })} subtitle={t('user.deactivateSub')} confirm={t('user.deactivateConfirm')} path={`/api/v1/users/${u.id}/deactivate`} body={{ expectedVersion: u.version }} onClose={() => setDeactivating(false)} onSaved={refresh} />
      ) : null}
      {sessionsOpen ? (
        <ReasonDialog title={t('user.sessionsTitle')} subtitle={t('user.sessionsSub', { name: u.displayName })} confirm={t('user.sessionsConfirm')} path={`/api/v1/users/${u.id}/sessions/revoke`} body={{}} onClose={() => setSessionsOpen(false)} onSaved={refresh} />
      ) : null}
      {resetting ? (
        <ReasonDialog<ActivationIssued>
          title={t('activation.issueTitle')}
          subtitle={t('activation.issueSub', { name: u.displayName })}
          confirm={t('activation.issueConfirm')}
          path={`/api/v1/users/${u.id}/activation`}
          body={{}}
          onClose={() => setResetting(false)}
          onSaved={refresh}
          onResult={(r) => setIssued(r)}
        />
      ) : null}
      {issued ? <ActivationLinkDialog name={u.displayName} activation={issued} onClose={() => setIssued(null)} /> : null}
      {mfaResetting ? (
        <ReasonDialog title={t('mfa.resetTitle', { name: u.displayName })} subtitle={t('mfa.resetSub')} confirm={t('mfa.resetConfirm')} path={`/api/v1/users/${u.id}/mfa/reset`} body={{}} onClose={() => setMfaResetting(false)} onSaved={refresh} />
      ) : null}
    </>
  );
}

function ReasonDialog<R = unknown>({
  title,
  subtitle,
  confirm,
  path,
  body,
  onClose,
  onSaved,
  onResult,
}: {
  title: string;
  subtitle: string;
  confirm: string;
  path: string;
  body: Record<string, unknown>;
  onClose: () => void;
  onSaved: () => void;
  onResult?: (r: R) => void;
}) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const m = useMutation({
    mutationFn: () => api<R>('POST', path, { ...body, reason }),
    onSuccess: (r) => {
      onSaved();
      onClose();
      onResult?.(r);
    },
  });
  const fe = m.error instanceof ApiRequestError ? m.error.fields : {};
  return (
    <Dialog open title={title} subtitle={subtitle} onClose={onClose}>
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        <ErrorSummary error={m.error} fieldIds={{ reason: 'reason-input' }} />
        <Field label={t('common.reason')} id="reason-input" error={fe.reason} full hint={t('common.reasonHint')}>
          {(p) => <textarea {...p} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />}
        </Field>
        <div className="form-actions">
          <button className="button" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="button danger" type="submit" disabled={m.isPending}>
            {m.isPending ? t('common.applying') : confirm}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function IssueGrantDialog({ user, mode, onClose, onSaved }: { user: UserDetailResponse; mode: 'experience' | 'specialist'; onClose: () => void; onSaved: () => void }) {
  const { session, can } = useSession();
  const { t, tk } = useI18n();
  const idem = useRef(newIdempotencyKey());
  const orgIssuer = mode === 'specialist' ? can('grant.sensitive.issue') : can('grant.issue');
  // PD-005: a site administrator only sees the sites where they may grant.
  const sites = orgIssuer ? session.sites : session.sites.filter((s) => can('grant.site.issue', s.id));
  const codes = mode === 'specialist' ? SPECIALISTS.map((s) => s.id) : orgIssuer ? GRANTABLE : SITE_ADMIN_GRANTABLE;
  const [code, setCode] = useState(codes.includes('reception') ? 'reception' : codes[0]!);
  const scopeRule = mode === 'specialist' ? (SPECIALISTS.find((s) => s.id === code)?.scope ?? 'either') : TENANT_WIDE_ALLOWED.has(code) && orgIssuer ? 'either' : 'site';
  const [siteId, setSiteId] = useState(sites[0]?.id ?? '');
  const effectiveSite = scopeRule === 'tenant' ? '' : scopeRule === 'site' && !siteId ? (sites[0]?.id ?? '') : siteId;
  const maxDays = session.policy.siteGrantMaxDays;
  const endRequired = !orgIssuer; // PD-006
  const [until, setUntil] = useState(() => (endRequired ? toLocalInput(new Date(Date.now() + Math.min(30, maxDays) * 86_400_000)) : ''));
  const [reason, setReason] = useState('');
  const m = useMutation({
    mutationFn: () =>
      api('POST', `/api/v1/users/${user.id}/grants`, { kind: mode, code, siteId: effectiveSite || null, validUntil: until ? new Date(until).toISOString() : null, reason }, { idempotencyKey: idem.current }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  const fe = m.error instanceof ApiRequestError ? m.error.fields : {};
  const ids = { code: 'grant-code', siteId: 'grant-site', validUntil: 'grant-until', reason: 'grant-reason' };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    m.mutate();
  };
  return (
    <Dialog open title={mode === 'specialist' ? t('grant.titleSpecialist', { name: user.displayName }) : t('grant.title', { name: user.displayName })} subtitle={t('grant.sub')} onClose={onClose}>
      <form onSubmit={submit} noValidate>
        <ErrorSummary error={m.error} fieldIds={ids} />
        <div className="formgrid">
          <Field label={mode === 'specialist' ? t('grant.specialist') : t('grant.experience')} id={ids.code} error={fe.code}>
            {(p) => (
              <select {...p} value={code} onChange={(e) => setCode(e.target.value)}>
                {codes.map((c) => (
                  <option key={c} value={c}>
                    {mode === 'specialist' ? tk(`specialist.${c}`) : tk(`role.${c}.title`)}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label={t('grant.scope')} id={ids.siteId} error={fe.siteId} hint={scopeRule === 'site' ? t('grant.scopeSiteOnly') : undefined}>
            {(p) => (
              <select {...p} value={effectiveSite} onChange={(e) => setSiteId(e.target.value)} disabled={scopeRule === 'tenant'}>
                {scopeRule !== 'site' ? <option value="">{t('grant.scopeOrg')}</option> : null}
                {scopeRule !== 'tenant'
                  ? sites.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))
                  : null}
              </select>
            )}
          </Field>
          <Field label={endRequired ? t('grant.ends') : t('grant.endsOptional')} id={ids.validUntil} error={fe.validUntil} hint={endRequired ? t('grant.endsRequired', { days: maxDays }) : t('grant.endsHint')}>
            {(p) => <input {...p} type="datetime-local" value={until} onChange={(e) => setUntil(e.target.value)} required={endRequired} />}
          </Field>
          <Field label={t('common.reason')} id={ids.reason} error={fe.reason} full>
            {(p) => <textarea {...p} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />}
          </Field>
        </div>
        {mode === 'experience' ? (
          <p className="note" style={{ marginBlockStart: 16 }}>
            {tk(`role.${code}.boundary`)}
          </p>
        ) : null}
        <div className="form-actions">
          <button className="button" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="button primary" type="submit" disabled={m.isPending}>
            {m.isPending ? t('grant.submitting') : mode === 'specialist' ? t('grant.submitSpecialist') : t('grant.submit')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function CreateUserDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (r: CreateUserResponse) => void }) {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const m = useMutation({ mutationFn: () => api<CreateUserResponse>('POST', '/api/v1/users', { email, displayName }), onSuccess: onCreated });
  const fe = m.error instanceof ApiRequestError ? m.error.fields : {};
  return (
    <Dialog open title={t('createUser.title')} subtitle={t('createUser.sub')} onClose={onClose}>
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        <ErrorSummary error={m.error} fieldIds={{ email: 'new-email', displayName: 'new-name' }} />
        <div className="formgrid">
          <Field label={t('createUser.email')} id="new-email" error={fe.email}>
            {(p) => <input {...p} type="email" dir="ltr" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={254} />}
          </Field>
          <Field label={t('createUser.name')} id="new-name" error={fe.displayName}>
            {(p) => <input {...p} value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={200} />}
          </Field>
        </div>
        <div className="form-actions">
          <button className="button" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="button primary" type="submit" disabled={m.isPending}>
            {m.isPending ? t('common.saving') : t('createUser.submit')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/** PD-009: the link is shown once; Passage never stores or re-displays it. */
export function ActivationLinkDialog({ name, activation, onClose, url }: { name: string; activation: ActivationIssued; onClose: () => void; url?: string }) {
  const { t, dateTime } = useI18n();
  const [copied, setCopied] = useState(false);
  const link = url ?? activationUrl(activation.activationToken);
  return (
    <Dialog open title={t('activation.title', { name })} onClose={onClose}>
      <p className="note warning">{t('activation.body', { name, expires: dateTime(activation.expiresAt) })}</p>
      <div className="activation-link">
        <input readOnly value={link} dir="ltr" aria-label={t('activation.title', { name })} onFocus={(e) => e.currentTarget.select()} />
        <button
          className="button"
          type="button"
          onClick={() => {
            void navigator.clipboard?.writeText(link).then(() => setCopied(true));
          }}
        >
          {copied ? t('common.copied') : t('common.copy')}
        </button>
      </div>
      <div className="form-actions">
        <button className="button primary" type="button" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>
    </Dialog>
  );
}
